import { CzechSuklRsAtcGroupsParams } from '~/libs/infrastructure/api/availability';

import { DEFAULT_PER_PAGE, PER_PAGE_OPTIONS } from '../constants';
import { FiltersValue } from '../components/Filters';
import { isUuid } from './availabilitySlug';

export type AvailabilitySearchToken = { kind: 'uuid'; value: string } | { kind: 'slug'; value: string };

export function parseSearchTokens(search: string): AvailabilitySearchToken[] {
	const queryParams = new URLSearchParams(search);
	const raw = queryParams.get('search') ?? queryParams.get('items') ?? '';

	return raw
		.split(/[\s,]+/)
		.filter(Boolean)
		.map((value) => (isUuid(value) ? { kind: 'uuid', value } : { kind: 'slug', value }));
}

// Builds the API request params for the ATC-groups list/export endpoints from the current filters.
export function buildAvailabilityParams(
	filters: FiltersValue,
	page: number,
	perPage: number
): CzechSuklRsAtcGroupsParams {
	return {
		page,
		perPage,
		ids: filters.items.length > 0 ? filters.items.map((item) => item.id) : undefined,
		rs: filters.rs.length > 0 ? filters.rs : undefined,
	};
}

export function parseFiltersFromSearch(search: string): FiltersValue {
	const queryParams = new URLSearchParams(search);
	const rs = (queryParams.get('rs') ?? '').split(',').filter(Boolean);

	return {
		items: parseSearchTokens(search).map((token) =>
			token.kind === 'uuid'
				? { id: token.value, label: token.value }
				: { id: token.value, label: token.value, slug: token.value }
		),
		rs,
	};
}

export function parsePageFromSearch(search: string): number {
	const queryParams = new URLSearchParams(search);
	return Number(queryParams.get('page')) || 1;
}

export function parsePerPageFromSearch(search: string): number {
	const queryParams = new URLSearchParams(search);
	const perPage = Number(queryParams.get('perPage'));
	return PER_PAGE_OPTIONS.includes(perPage) ? perPage : DEFAULT_PER_PAGE;
}

// Serializes the current filters/pagination into a URL query string, preserving any unrelated
// params already present in `currentSearch`.
export function buildAvailabilitySearch(
	currentSearch: string,
	filters: FiltersValue,
	page: number,
	perPage: number
): string {
	const queryParams = new URLSearchParams(currentSearch);

	if (filters.items.length > 0) {
		queryParams.set('search', filters.items.map((item) => item.slug ?? item.id).join(' '));
	} else {
		queryParams.delete('search');
	}
	queryParams.delete('items');
	queryParams.delete('filter');

	if (filters.rs.length > 0) {
		queryParams.set('rs', filters.rs.join(','));
	} else {
		queryParams.delete('rs');
	}

	queryParams.delete('year');

	if (page > 1) {
		queryParams.set('page', page.toString());
	} else {
		queryParams.delete('page');
	}

	if (perPage !== DEFAULT_PER_PAGE) {
		queryParams.set('perPage', perPage.toString());
	} else {
		queryParams.delete('perPage');
	}

	return queryParams.toString();
}
